/* eslint-disable */
import { useRef, useMemo, useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { Mail, Send, FileText, Paperclip } from "lucide-react";
import { Account } from "@features/cx/outstanding/types";
import {
  useChannelChoices,
  useNoticeTemplates,
  useSaveBulkCommunicationStep3,
} from "@features/cx/outstanding/hooks";
import {
  useCurrency,
  useRemoteUtilityId,
} from "@shared/selectors/globalSelectors";
import { logEvent } from "@shared/analytics/analytics";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { FIELD_CLASSES } from "@shared/utils/constants";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  account: Account;
  outstandingAmount: number;
}

interface NoticeFormValues {
  channel: string;
  template: string;
  attachedFileName: string;
}

export function SendNoticeDialog({
  isOpen,
  onClose,
  account,
  outstandingAmount,
}: Props) {
  console.log("account", account);
  const currency = useCurrency();
  const remoteUtilityId = useRemoteUtilityId();
  const saveStep3 = useSaveBulkCommunicationStep3();

  const [selectedChannelType, setSelectedChannelType] = useState<string>("");
  const { data: channelList } = useChannelChoices("quick_messaging");
  const { data: templateList } = useNoticeTemplates(selectedChannelType);

  const channelChoices = useMemo(
    () =>
      channelList?.map((data: any) => ({
        value: data.key,
        label: data.value,
      })) || [],
    [channelList],
  );

  const templateChoices = useMemo(() => {
    return (
      templateList?.map((temp: any) => ({
        label: temp.templateName,
        value: temp.id,
      })) || []
    );
  }, [templateList]);

  // Keep the real File object outside of form state (not serialisable)
  const attachedFileRef = useRef<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const customerName =
    account?.customerName ||
    `${account?.firstName ?? ""} ${account?.lastName ?? ""}`.trim() ||
    "Customer";
  const accountNo = account?.consumerNo || account?.accountNumber || "N/A";
  const email = account?.email || "N/A";
  const amountDisplay = `${currency} ${Number(outstandingAmount ?? 0).toLocaleString()}`;

  // ─── Form fields ────────────────────────────────────────────────────────────
  const fields: FormField[] = useMemo(
    () => [
      // Consumer info banner
      {
        name: "consumerInfoBanner",
        type: "custom",
        className: "col-span-12",
        customRender: () => (
          <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5 text-sm">
            <span className="text-gray-500">
              Consumer:{" "}
              <span className="font-semibold text-gray-800">
                {customerName} ({accountNo})
              </span>
            </span>
            <span className="text-gray-500">
              Outstanding:{" "}
              <span className="font-semibold text-gray-800">
                {amountDisplay}
              </span>
            </span>
          </div>
        ),
      },
      // Channel selector
      {
        name: "channel",
        label: "Message Channel",
        type: "select",
        required: true,
        placeholder: "Select a channel",
        helperText: "Select a channel for the notice",
        className: "col-span-12",
        options: channelChoices,
      },
      // Template selector
      {
        name: "template",
        label: "Template",
        type: "select",
        required: true,
        placeholder: "Select a template",
        helperText: "Select a template for the notice",
        className: "col-span-12",
        options: templateChoices,
      },
      // Attachment upload
      {
        name: "attachedFileName",
        type: "custom",
        className: "col-span-12",
        customRender: (_field: FormField, form: any) => {
          const fileName = form?.watch("attachedFileName") || "";

          const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0] ?? null;
            if (file) {
              const allowedTypes = ["application/pdf", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document", "image/png", "image/jpeg"];
              if (!allowedTypes.includes(file.type)) {
                e.target.value = "";
                return;
              }
            }
            attachedFileRef.current = file;
            form?.setValue("attachedFileName", file ? file.name : "");
          };

          return (
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700">
                Attachment
              </label>
              {/* Hidden native input */}
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={handleFileChange}
                accept=".pdf,.doc,.docx,.png,.jpg"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-blue-400 hover:text-blue-500 hover:bg-blue-50 transition-colors"
              >
                <Paperclip className="h-4 w-4" />
                {fileName || "Upload File"}
              </button>
            </div>
          );
        },
        helperText: "Upload any relevant document",
      },
      // Live preview
      {
        name: "previewBlock",
        type: "custom",
        className: "col-span-12",
        customRender: (_field: FormField, form: any) => {
          const selectedTemplate = form?.watch("template");
          const tpl = templateList?.find(
            (temp: any) =>
              temp.id === Number(selectedTemplate) ||
              temp.id === selectedTemplate,
          );
          const previewSubject = tpl?.content?.subject || "Collection Notice";
          const previewBody = tpl?.content?.body || "";

          return (
            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                <FileText className="h-4 w-4 text-gray-400" />
                Preview
              </label>
              <div className="border border-gray-200 rounded-lg bg-gray-50 px-4 py-3 space-y-2 text-sm">
                <p className="font-semibold text-gray-800">
                  Subject: {previewSubject}
                </p>
                <hr className="border-gray-200" />
                <div
                  className="text-gray-600 whitespace-pre-line leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: previewBody }}
                />
              </div>
            </div>
          );
        },
      },
    ],
    [
      customerName,
      accountNo,
      amountDisplay,
      channelChoices,
      templateChoices,
      templateList,
    ],
  );

  // ─── Form instance ───────────────────────────────────────────────────────────
  const form = FormService.createForm<NoticeFormValues>({
    fields,
    mode: "onChange",
    defaultValues: {
      channel: "",
      template: "",
      attachedFileName: "",
    },
  });

  const currentChannel = form.watch("channel");

  useEffect(() => {
    if (currentChannel !== undefined && currentChannel !== null) {
      const str = currentChannel.toString().toUpperCase();
      if (str === "0" || str === "EMAIL") {
        setSelectedChannelType("EMAIL");
      } else if (
        str === "1" ||
        str === "SMS" ||
        str === "TEXT MESSAGE" ||
        str === "TEXT_MESSAGE"
      ) {
        setSelectedChannelType("SMS");
      } else {
        setSelectedChannelType("");
      }
      form.setValue("template", "");
    }
  }, [currentChannel, form]);

  // ─── Submit ──────────────────────────────────────────────────────────────────
  const handleSubmit = (data: NoticeFormValues) => {
    const selectedTpl = templateList?.find(
      (t: any) => String(t.id) === String(data.template)
    );
    const templateName = selectedTpl ? selectedTpl.templateName : "";

    const formData = new FormData();
    formData.append("consumer_ids", JSON.stringify([Number(account.id)]));
    formData.append("batch_type", "1");
    formData.append("channel_type", String(data.channel));
    formData.append("template_id", String(Number(data.template)));
    formData.append("template_name", templateName);
    formData.append("total_outstanding", Number(outstandingAmount || 0).toFixed(2));
    formData.append("approval_notes", "");
    formData.append("remarks", "");
    formData.append("remote_utility_id", String(remoteUtilityId));

    // Append attachment from dialog file upload
    if (attachedFileRef.current) {
      formData.append("attachments", attachedFileRef.current);
    }

    // Append attachments if any from window object store
    const fileObjects = (window as any).bulkNoticeFileData?.fileObjects || [];
    fileObjects.forEach((file: File) => {
      formData.append("attachments", file);
    });

    saveStep3.mutateAsync(formData, {
      onSuccess: () => {
        logEvent("Notice Generated", { channelType: selectedChannelType });
      },
    });
    onClose();
  };

  // ─── Custom footer ───────────────────────────────────────────────────────────
  const renderCustomFooter = () => (
    <div className="flex items-center justify-end pt-3 border-t bg-gray-50 -mx-6 px-6 -mb-5 py-3 mt-2">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={form.handleSubmit(handleSubmit)}
          disabled={saveStep3.isPending || !form.formState.isValid}
        >
          <Send className="mr-1.5 h-3.5 w-3.5" />
          {selectedChannelType === "SMS" ? "Send SMS" : "Send Email"}
        </Button>
      </div>
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] p-0 gap-0 overflow-hidden rounded-xl">
        {/* Header */}
        <DialogHeader className="px-6 py-4 border-b">
          <DialogTitle className="flex items-center gap-2 text-[15px] font-semibold text-gray-900">
            <Mail className="h-4 w-4 text-gray-500" />
            Send Collection Notice
          </DialogTitle>
        </DialogHeader>

        <div className="px-6 py-5 max-h-[70vh] overflow-y-auto">
          <DynamicForm
            fields={fields}
            form={form}
            onSubmit={handleSubmit}
            config={{
              showSubmitButton: false,
              showResetButton: false,
              showProgress: false,
              classes: FIELD_CLASSES,
            }}
            gridColumns={12}
            responsiveColumns={{ sm: 12, md: 12 }}
            renderCustomFooter={renderCustomFooter}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
